import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { ContractSession, ContractSessionResponse, ContractDetails, ContractDetailsResponse, ContractSessionListResponse, BIPConfirmation, CheckSendToCorpResponse } from '../models/contractsession.model';
import { Lawfirm } from '../models/lawfirm.model';
import { delayedRetryHttp } from '../shared/delayedRetry';
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { UtilityService } from './shared/utility.service';


@Injectable()
export class ContractSessionService {
    private baseUrl: string;
    private api: string = "api/ContractSession";

    constructor(private httpClient: HttpClient, @Inject('BASE_URL') _baseUrl: string, private _utilityService: UtilityService) {
        this.baseUrl = _baseUrl + this.api;
    }


    private handleErrors(errorResponse: HttpErrorResponse) {
        if (errorResponse.error instanceof ErrorEvent) {
            console.error('Contract Session Service Client Side Error: ', errorResponse.error.message);
        } else {
            throw errorResponse;
        }
    }

    async createContactSessionAsync(encryptedUser: string, contractSession: ContractSession): Promise<ContractSession | void> {
        const a = await this.httpClient.post<ContractSession>(`${this.baseUrl}`, contractSession, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractSession, "", encryptedUser, "ContractSessionService", "createContactSessionAsync"); });

        return a;
    }

    async getContactSessionAsync(encryptedUser: string, inventionId: number): Promise<ContractSessionListResponse | void> {
        const a = await this.httpClient
            .get<ContractSessionListResponse>(`${this.baseUrl}/GetContractSession/${inventionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "InventionID - " + inventionId, encryptedUser, "ContractSessionService", "getContactSessionAsync"); });

        return a;
    }

    async getContactSessionByCSIdAsync(encryptedUser: string, contractSessionId: number): Promise<ContractSession | void> {
        const a = await this.httpClient
            .get<ContractSession>(`${this.baseUrl}/GetContractSessionByCSId/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser,
                    'Retry-After': '15'
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "ContractSessionService", "getContactSessionByCSIdAsync"); });

        return a;
    }

    getContractDetailsAsync(encryptedUser: string, clpUserId: number, contactId: number, contractType: string): Observable<ContractDetailsResponse> {
        return this.httpClient
            .get<ContractDetailsResponse>(`${this.baseUrl}/GetContractDetails/${clpUserId}/${contactId}/${contractType}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp(10000), catchError(err => { return this._utilityService.handleErrors(err, null, "clpUserId - " + clpUserId + " contactId - " + contactId, encryptedUser, "ContractSessionService", "getContractDetailsAsync"); }));
    }

    //async getContractDetailsAsync(encryptedUser: string, clpUserId: number, contactId: number): Promise<ContractDetailsResponse | void> {
    //    const a = await this.httpClient
    //        .get<ContractDetailsResponse>(`${this.baseUrl}/GetContractDetails/${clpUserId}/${contactId}`, {
    //            headers: new HttpHeaders({
    //                'Content-Type': 'application/json',
    //                'Authorization': 'Basic ' + encryptedUser
    //            })
    //        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "clpUserId - " + clpUserId + " contactId - " + contactId, encryptedUser, "ContractSessionService", "getContractDetailsAsync"); });

    //    return a;
    //}

    async getLawfirmListAsync(encryptedUser: string): Promise<Lawfirm[] | void> {
        const a = await this.httpClient
            .get<Lawfirm[]>(`${this.baseUrl}/GetLawfirms`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "ContractSessionService", "getLawfirmListAsync"); });

        return a;
    }

    async getBIPConfirmationByCSIdAsync(encryptedUser: string, contractSessionId: number): Promise<BIPConfirmation | void> {
        const a = await this.httpClient
            .get<BIPConfirmation>(`${this.baseUrl}/GetBIPConfirmationByCSId/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser,
                    'Retry-After': '15'
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "ContractSessionService", "getBIPConfirmationByCSIdAsync"); });

        return a;
    }

    async createBIPConfirmationAsync(encryptedUser: string, bipconfirmation: BIPConfirmation): Promise<BIPConfirmation | void> {
        const a = await this.httpClient.post<BIPConfirmation>(`${this.baseUrl}/CreateBIPConfirmation`, bipconfirmation, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, bipconfirmation, "", encryptedUser, "ContractSessionService", "createBIPConfirmationAsync"); });

        return a;
    }

    async createContractReceiveAsync(encryptedUser: string, contractSessionId: number, clpUserId: number): Promise<number | void> {
        const a = await this.httpClient.post<number>(`${this.baseUrl}/CreateContractReceive/${contractSessionId}/${clpUserId}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractSessionId, "", encryptedUser, "ContractSessionService", "createContractReceiveAsync"); });

        return a;
    }

    async createContractApprovalAsync(encryptedUser: string, contractSessionId: number, clpUserId: number): Promise<number | void> {
        const a = await this.httpClient.post<number>(`${this.baseUrl}/CreateContractApproval/${contractSessionId}/${clpUserId}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractSessionId, "", encryptedUser, "ContractSessionService", "createContractApprovalAsync"); });

        return a;
    }

    async getContractSessionAuditSendToCorpAsync(encryptedUser: string, contractSessionId: number): Promise<ContractSession | void> {
        const a = await this.httpClient
            .get<ContractSession>(`${this.baseUrl}/GetContractSessionAuditSendToCorp/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser,
                    'Retry-After': '15'
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "ContractSessionService", "getContractSessionAuditSendToCorpAsync"); });

        return a;
    }

    async checkSendToCorp_GetAsync(encryptedUser: string, contractSessionId: number): Promise<CheckSendToCorpResponse | void> {
        const a = await this.httpClient
            .get<CheckSendToCorpResponse>(`${this.baseUrl}/CheckSendToCorpProcess_Get/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "ContractSessionService", "checkSendToCorp_GetAsync"); });

        return a;
    }
}
